import React, { useEffect, useState } from 'react';

import { useStorage } from '@playbooks/contexts';
import { logger } from '@playbooks/utils/logger';

type ThemePreference = 'light' | 'dark' | 'system';

type ThemeProps = {
	preference: ThemePreference;
	setPreference: (preference: ThemePreference) => void;
	isDark: boolean;
	loaded: boolean;
};

const ThemeContext = React.createContext<ThemeProps>(null);

const ThemeProvider = ({ children }) => {
	const storage = useStorage();
	const [preference, setPreference] = useState<ThemePreference>(storage.storage.preference || 'system');
	const [theme, setTheme] = useState(storage.storage.theme || '');
	const [loaded, setLoaded] = useState(false);

	// Computed
	const isDark = theme === 'dark';
	// Hooks
	useEffect(() => {
		setLoaded(true);
	}, []);

	useEffect(() => {
		changePreference(preference);
	}, [preference]);

	useEffect(() => {
		if (preference !== 'system') return;
		const media = window.matchMedia('(prefers-color-scheme: dark)');
		const onChange = event => changeTheme(event.matches ? 'dark' : 'light');
		media.addEventListener('change', onChange);
		return () => media.removeEventListener('change', onChange);
	}, [preference]);

	useEffect(() => {
		if (loaded) logger.debug('themeContext:', { theme });
	}, [loaded, theme]);

	// Methods
	const changePreference = (preference: ThemePreference) => {
		storage.storeValue('preference', preference);
		storage.storeCookie('preference', preference);
		if (preference === 'system') {
			const systemPreference = window.matchMedia('(prefers-color-scheme:dark)').matches;
			changeTheme(systemPreference ? 'dark' : 'light');
		} else {
			changeTheme(preference);
		}
	};

	const changeTheme = theme => {
		storage.storeValue('theme', theme);
		storage.storeCookie('theme', theme);
		const root = document.documentElement;
		theme === 'dark' ? root.classList.add('dark') : root.classList.remove('dark');
		setTheme(theme);
	};

	return (
		<ThemeContext.Provider value={{ preference, setPreference, isDark, loaded }}>{children}</ThemeContext.Provider>
	);
};

const useTheme = () => {
	return React.useContext(ThemeContext);
};

export { ThemeProvider, useTheme };
